// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useFulfilSettlementObligation } from '@hooks/mutations/collateral/settlmentObligation/useFulfilSettlementObligation';
import dayjs from 'dayjs';
import { FC, useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import {
  CollateralAgreement,
  CollateralAgreement_SettlementObligation,
} from '@daml.js/utility-collateral-app-v2/lib/Utility/Collateral/App/Model/Collateral';
import { ContractId } from '@daml/types';
import FormDialog from '../../../../components/Dialog/FormDialog';
import { TextInputLabel } from '../../../../components/Form/TextInput';
import { AssetSelect } from '../../common/AssetSelect/AssetSelect';
import { MarketDataForm } from '../../common/marketData/MarketDataForm';
import { useConvertToMarketData } from '../../common/marketData/useConvertToMarketData';
import { useGetCollateralAgreement } from '../../common/useGetCollateralAgreement';
import { useFulfilSettlementObligationForm } from './useFulfilSettlementObligationForm';

type FulfilSettlementObligationDialogProps = {
  settlementObligationCId: ContractId<CollateralAgreement_SettlementObligation>;
  collateralAgreementCId: ContractId<CollateralAgreement>;
  open: boolean;
  onClose: () => void;
};

export const FulfilSettlementObligationDialog: FC<FulfilSettlementObligationDialogProps> = ({
  settlementObligationCId,
  collateralAgreementCId,
  open,
  onClose,
}) => {
  const convertToMarketData = useConvertToMarketData();
  const { getCollateralAgreementByContractId } = useGetCollateralAgreement();

  const collateralAgreement = useMemo(() => {
    const agreement = getCollateralAgreementByContractId(collateralAgreementCId);

    if (!agreement) {
      throw new Error(`Collateral agreement not found for Contract Id: ${collateralAgreementCId}!`);
    }

    return agreement;
  }, [getCollateralAgreementByContractId, collateralAgreementCId]);

  const form = useFulfilSettlementObligationForm(collateralAgreement);

  const fulfilSettlementObligation = useFulfilSettlementObligation();

  const onSubmit = useCallback(async () => {
    const createdAt = dayjs().toISOString();

    const positions = form.positions.map((p) => ({
      instrument: { admin: p.admin, id: p.id },
      amount: p.amount,
    }));

    await fulfilSettlementObligation({
      cid: settlementObligationCId,
      argument: {
        positions,
        marketData: convertToMarketData(form.marketData, createdAt),
      },
    });
    onClose();
  }, [form, onClose, settlementObligationCId, convertToMarketData, fulfilSettlementObligation]);

  return (
    <FormDialog
      open={open}
      title="Fulfil Settlement Obligation"
      onCancel={onClose}
      onSubmit={onSubmit}
      submitName="Fulfil"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
        <TextInputLabel label="Positions" required />
      </Box>
      <AssetSelect
        onAdd={form.addPosition}
        onRemove={form.removePosition}
        onAmountChange={form.updatePositionAmount}
        onInstrumentChange={form.updatePositionInstrument}
        rows={form.positions}
        eligibleInstruments={form.eligibleInstruments.all}
      />
      <Box sx={{ mb: 1 }}>
        <TextInputLabel label="Market Data" required />
        <MarketDataForm
          value={form.marketData}
          onAdd={form.addPricingData}
          onUpdate={form.updatePricingData}
          onRemove={form.removePricingData}
          eligibleInstruments={form.eligibleInstruments.all}
        />
      </Box>
    </FormDialog>
  );
};
